import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate } from '../../lib/format';
import type { ApiKeySummary } from '../../lib/types';
import {
  Badge,
  Button,
  DataTable,
  ErrorBanner,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';

export function ApiKeysPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { hasModule, isLoading: pkgLoading } = useClientPackage();
  const keysQuery = useQuery({ queryKey: ['api-keys'], queryFn: api.apiKeys.list });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.apiKeys.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: courtesy-only gate, see PromoCodesPage.tsx's
  // identical comment. Module key is `publicApiAccess` per §6's schema.
  if (!pkgLoading && !hasModule('publicApiAccess')) {
    return (
      <div>
        <PageHeader title="API keys" />
        <ModuleNotIncluded feature="API keys" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="API keys"
        description="Secret keys authenticate server-to-server calls; public keys are safe for browser code."
        actions={
          <RequirePermission permission="api_keys.manage">
            <Button onClick={() => setCreateOpen(true)}>Create key</Button>
          </RequirePermission>
        }
      />
      {keysQuery.isLoading ? (
        <Spinner />
      ) : keysQuery.error ? (
        <ErrorBanner
          message={
            keysQuery.error instanceof Error ? keysQuery.error.message : 'Failed to load API keys.'
          }
        />
      ) : (
        <DataTable<ApiKeySummary>
          columns={[
            { key: 'name', header: 'Name', render: (k) => k.name },
            {
              key: 'prefix',
              header: 'Key',
              render: (k) => <span className="font-mono text-xs">{k.keyPrefixVisible}...</span>,
            },
            { key: 'type', header: 'Type', render: (k) => <Badge>{k.type}</Badge> },
            { key: 'mode', header: 'Mode', render: (k) => k.mode },
            { key: 'lastUsed', header: 'Last used', render: (k) => formatDate(k.lastUsedAt) },
            {
              key: 'status',
              header: 'Status',
              render: (k) =>
                k.revokedAt ? (
                  <Badge tone="danger">Revoked</Badge>
                ) : (
                  <Badge tone="success">Active</Badge>
                ),
            },
            {
              key: 'actions',
              header: '',
              render: (k) =>
                !k.revokedAt && (
                  <RequirePermission permission="api_keys.manage">
                    <Button size="sm" variant="ghost" onClick={() => revokeMutation.mutate(k.id)}>
                      Revoke
                    </Button>
                  </RequirePermission>
                ),
            },
          ]}
          rows={keysQuery.data?.data ?? []}
          getRowKey={(k) => k.id}
          emptyMessage="No API keys yet."
        />
      )}
      <CreateApiKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={setRevealedKey}
      />
      <RevealKeyModal revealedKey={revealedKey} onClose={() => setRevealedKey(null)} />
    </div>
  );
}

/**
 * Reveal-once screen (work order: "show the secret ONCE... copy it now, you will not see it
 * again"). The key only ever lives in `ApiKeysPage`'s `useState` (never localStorage/
 * sessionStorage/a URL) and is discarded the moment this modal closes - closing re-opening it
 * does not bring the value back, a fresh key must be created to see a new one.
 */
function RevealKeyModal({
  revealedKey,
  onClose,
}: {
  revealedKey: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey);
      setCopied(true);
    } catch {
      // Clipboard API can be unavailable (insecure context, permission denied) - the key is still
      // right there to select and copy by hand, so this isn't a hard failure.
    }
  }

  return (
    <Modal
      open={!!revealedKey}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title="Your new API key"
      footer={
        <Button
          onClick={() => {
            setCopied(false);
            onClose();
          }}
        >
          Done - I've saved it
        </Button>
      }
    >
      <p className="text-sm text-slate-600 mb-2">
        Copy this now. For your security, you will not be able to see it again - if you lose it,
        you'll need to create a new key.
      </p>
      <div className="flex items-start gap-2">
        <code className="block flex-1 break-all bg-slate-100 rounded-brand p-3 text-sm">
          {revealedKey}
        </code>
        <Button variant="secondary" size="sm" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </Modal>
  );
}

function CreateApiKeyModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (rawKey: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<'secret' | 'public'>('secret');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  // REAL BUG FOUND AND FIXED: this form used to always send `scopes: []` - the API's
  // createApiKeySchema requires `scopes: z.array(z.string()).min(1)`, so every single API key
  // creation attempt through this screen has always failed with a validation error, 100% of the
  // time, regardless of what the user filled in. No scope picker existed at all.
  const [scopes, setScopes] = useState<string[]>([]);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  function toggleScope(scope: string) {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  }

  const mutation = useMutation({
    mutationFn: () => api.apiKeys.create({ name, type, mode, scopes }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      onClose();
      onCreated(created.rawKey);
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create API key"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || scopes.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Name" hint="A label to identify this key later">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Zapier integration"
        />
      </FormField>
      <FormField label="Type">
        <Select value={type} onChange={(e) => setType(e.target.value as 'secret' | 'public')}>
          <option value="secret">Secret (server-side)</option>
          <option value="public">Public (browser-safe)</option>
        </Select>
      </FormField>
      <FormField label="Mode">
        <Select value={mode} onChange={(e) => setMode(e.target.value as 'test' | 'live')}>
          <option value="test">Test</option>
          <option value="live">Live</option>
        </Select>
      </FormField>
      <FormField
        label="Permissions"
        hint="What this key can do - at least one is required"
        error={scopes.length === 0 ? 'Choose at least one permission.' : undefined}
      >
        <div className="flex flex-col gap-1 rounded-brand border border-slate-200 p-2 max-h-40 overflow-y-auto">
          {SCOPE_OPTIONS.map((scope) => (
            <label key={scope} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
              <code className="text-xs">{scope}</code>
            </label>
          ))}
        </div>
      </FormField>
    </Modal>
  );
}

/** A deliberately curated subset of TENANT_PERMISSIONS (@loctopus/rbac) - the ones that make
 * sense for an API key (not staff/role-management permissions, which stay human-only). */
const SCOPE_OPTIONS = [
  'orders.view',
  'orders.refund',
  'orders.export',
  'tickets.resend',
  'tickets.check_in',
  'tickets.transfer',
  'events.manage',
  'events.publish',
  'reports.view',
  'payouts.view',
  'invoices.view',
  'webhooks.manage',
];
